import React, { FC, useCallback, useEffect, useState } from "react";

import { OverlayTrigger, Table, Tooltip } from "react-bootstrap";

import classNames from "classnames";
import { isNil } from "lodash";

import { ParticipantTypes, PendingApprovalUsers } from "@api/graphql";
import ParticipantName from "@components/account_profile/ParticipantName";
import { ParticipantStatusBadge } from "@components/account_profile/ParticipantStatusBadge";
import { TableContents, TableHeader } from "@components/table/TableContents";
import { TableHeaders as CustomTableHeaders } from "@components/table/TableHeaders";
import { formatDateStringLong, getParticipantTypesForDisplay } from "@util/index";

import styles from "./ParticipantTable.module.scss";

const DEFAULT_TABLE_HEADERS: TableHeader<PendingApprovalUsers>[] = [
  { label: "Account ID", accessor: "participant_id" },
  { label: "Account name", accessor: "account_name" },
  { label: "User name", accessor: "full_name" },
  { label: "Account type", accessor: "participant_type" },
  { label: "Join date", accessor: "date_created" },
  { label: "Account status", accessor: "user_status" },
];

interface ProvisionalResaleTableProps {
  users: PendingApprovalUsers[];
  onRowClick?: (rowData: PendingApprovalUsers) => void;
  customTableHeaders?: TableHeader<PendingApprovalUsers>[];
  customTableStyleClassName?: string;
}

const WithTooltip = ({ children, label }: { children: React.ReactElement; label: string }) => {
  return (
    <OverlayTrigger placement="top-start" overlay={<Tooltip>{label}</Tooltip>}>
      {children}
    </OverlayTrigger>
  );
};

export const UsersTable: FC<ProvisionalResaleTableProps> = ({
  users,
  onRowClick,
  customTableHeaders,
  customTableStyleClassName,
}) => {
  const [sortKey, setSortKey] = useState<keyof PendingApprovalUsers>("user_status");
  const [sortDirection, setSortDirection] = useState<"ascending" | "descending">("ascending");
  const [sortedData, setSortedData] = useState(users);

  const mapColumnValue = useCallback(
    (
      value: PendingApprovalUsers[keyof PendingApprovalUsers],
      key: keyof PendingApprovalUsers,
      row?: PendingApprovalUsers,
    ) => {
      const text = isNil(value) ? "" : value;

      let component = (
        <td className="align-content-center" key={String(key)}>
          <span>{text}</span>
        </td>
      );

      if (row) {
        const { participant_id, full_name, participant_type, user_status, account_name, date_created } = row;

        if (key === "participant_id") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <WithTooltip label={text.toString()}>
                <span>{text}</span>
              </WithTooltip>
            </td>
          );
        } else if (key === "account_name") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <ParticipantName name={account_name || ""} participantId={participant_id} defaultTab="AccountDetails" />
            </td>
          );
        } else if (key === "full_name") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <ParticipantName
                name={full_name}
                participantId={participant_id}
                defaultTab="AccountDetails"
                disableClick
              />
            </td>
          );
        } else if (key === "user_status") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div className={classNames(styles["badge-cell"], styles["badge-truncate"])}>
                <ParticipantStatusBadge status={user_status} />
              </div>
            </td>
          );
        } else if (key === "participant_type") {
          component = (
            <td className="align-content-center" key={String(key)}>
              <div>{getParticipantTypesForDisplay(participant_type as ParticipantTypes[])}</div>
            </td>
          );
        } else {
          // Case key == "date_created"
          component = (
            <td className="align-content-center" key={String(key)}>
              <div>{formatDateStringLong(date_created.substring(0, 10))}</div>
            </td>
          );
        }
      }
      return { component };
    },
    [],
  );

  useEffect(() => {
    const sortedData = [...users].sort((a, b) => {
      const aValue = a[sortKey];
      const bValue = b[sortKey];
      if (sortDirection === "ascending") {
        return aValue! > bValue! ? 1 : -1;
      }
      return aValue! < bValue! ? 1 : -1;
    });
    setSortedData(sortedData);
  }, [users, sortDirection, sortKey]);

  const handleSort = (newSortKey: keyof PendingApprovalUsers) => {
    if (sortKey === newSortKey) {
      setSortDirection((prevDirection) => (prevDirection === "ascending" ? "descending" : "ascending"));
    } else {
      setSortKey(newSortKey);
      setSortDirection("ascending");
    }
  };

  return (
    <Table borderless hover className={customTableStyleClassName}>
      <CustomTableHeaders
        headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
        customStyles={styles}
        sortColumn={sortKey}
        sortDirection={sortDirection}
        setSortColumn={handleSort}
      />

      <TableContents<PendingApprovalUsers>
        headers={customTableHeaders ?? DEFAULT_TABLE_HEADERS}
        data={sortedData}
        keyAccessor="participant_id"
        customStyles={styles}
        mapColumnValue={mapColumnValue}
        onRowClick={onRowClick}
      />
    </Table>
  );
};
